import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
} from "chart.js";
import { useTranslation } from "react-i18next";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { MediaContext } from "../../context/MediaContext";
import { SocketContext } from "../../context/SocketContext";
import { BarChart, ZoomButton } from "./Charts.style";

ChartJS.register(LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, zoomPlugin);

const AsyncBarChart = ({ chapterize }) => {
  const { t } = useTranslation("", { keyPrefix: "components.charts" });

  const { player } = useContext(MediaContext);
  const { asyncUnderstandings, bookmarks } = useContext(SocketContext);
  const duration = useRef(60 * 60);
  const chartRef = useRef();
  const [deltaTime, setDeltaTime] = useState(duration.current / 6);
  const [chartData, setChartData] = useState({ labels: [], low: [], mid: [], high: [], max: 1 });

  const formatSeconds = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    let secs = Math.floor(seconds % 60);
    if (secs < 10) secs = "0" + secs;
    return `${minutes}:${secs}`;
  };

  const prepareTimedData = useCallback(() => {
    const data = { labels: [], low: [], mid: [], high: [] };
    let maxValue = 1;
    for (let i = 0; i < duration.current; i = i + deltaTime) {
      const bottom = i,
        top = i + deltaTime;
      const filtered = asyncUnderstandings.filter((u) => u.videoTs >= bottom && u.videoTs < top);
      data.labels.push(`${formatSeconds(bottom)}-${formatSeconds(top)}`);
      const low = filtered.filter((u) => u.value === 1).length;
      data.low.push(low);
      if (low > maxValue) maxValue = low;
      const mid = filtered.filter((u) => u.value === 2).length;
      data.mid.push(mid);
      if (mid > maxValue) maxValue = mid;
      const high = filtered.filter((u) => u.value === 3).length;
      data.high.push(high);
      if (high > maxValue) maxValue = high;
    }
    data.max = maxValue;
    return data;
  }, [asyncUnderstandings, deltaTime]);

  const prepareBookmarksData = useCallback(() => {
    const data = { labels: [], low: [], mid: [], high: [] };
    let maxValue = 1;
    for (let i = 0; i < bookmarks.length; i++) {
      data.labels.push(bookmarks[i].name);
      const bottom = bookmarks[i].videoTs;
      const top = i === bookmarks.length - 1 ? duration.current : bookmarks[i + 1].videoTs;
      const filtered = asyncUnderstandings.filter((u) => u.videoTs >= bottom && u.videoTs < top);
      const low = filtered.filter((u) => u.value === 1).length;
      data.low.push(low);
      if (low > maxValue) maxValue = low;
      const mid = filtered.filter((u) => u.value === 2).length;
      data.mid.push(mid);
      if (mid > maxValue) maxValue = mid;
      const high = filtered.filter((u) => u.value === 3).length;
      data.high.push(high);
      if (high > maxValue) maxValue = high;
    }
    data.max = maxValue;
    return data;
  }, [asyncUnderstandings, bookmarks]);

  const onZoom = (zoomIn) => {
    if (zoomIn) {
      if (deltaTime < duration.current) setDeltaTime((dt) => dt * 2);
    } else {
      if (deltaTime > 60) setDeltaTime((dt) => dt / 2);
    }
  };

  useEffect(() => {
    if (player) {
      chapterize && bookmarks.length > 0 ? setChartData(prepareBookmarksData()) : setChartData(prepareTimedData());
    }
  }, [bookmarks, chapterize, player, prepareBookmarksData, prepareTimedData]);

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        type: "bar",
        label: t("not_clear"),
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 2,
        fill: false,
        data: chartData.low,
        icon: "😵",
      },
      {
        type: "bar",
        label: t("half_clear"),
        backgroundColor: "rgb(255, 205, 86, 0.2)",
        borderColor: "rgb(255, 205, 86, 1)",
        borderWidth: 2,
        fill: false,
        data: chartData.mid,
        icon: "😐",
      },
      {
        type: "bar",
        label: t("clear"),
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2,
        fill: false,
        data: chartData.high,
        icon: "😀",
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 0,
        max: chartData.max + 1,
        ticks: {
          stepSize: 1,
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
      title: {
        display: false,
      },
      zoom: {
        limits: {
          x: { min: 0, max: duration.current, minRange: 5 },
        },
        zoom: {
          wheel: {
            enabled: true, // SET SCROOL ZOOM TO TRUE
          },
          mode: "x",
          speed: 0.1,
        },
        pan: {
          enabled: true,
          mode: "x",
        },
      },
      datalabels: {
        formatter: function (value, context) {
          return value /* + " " + context.dataset.icon */;
        },
        color: function (context) {
          return context.dataset.borderColor;
        },
        align: "top",
        anchor: "end",
        padding: -4,
        font: {
          size: "20",
          weight: "bold",
        },
      },
    },
  };
  return (
    <>
      <BarChart>
        {!chapterize && (
          <div className="btn-section">
            <ZoomButton side="left" onClick={() => onZoom(false)}>
              <i className="fas fa-search-minus" />
            </ZoomButton>
            <ZoomButton onClick={() => onZoom(true)}>
              <i className="fas fa-search-plus" />
            </ZoomButton>
          </div>
        )}
        <Bar ref={chartRef} options={options} data={data} plugins={[ChartDataLabels]} />
      </BarChart>
    </>
  );
};

export default AsyncBarChart;
